export type ChartCrimeGroup = "Visual error" | "Comparison flag";

export interface ChartCrimeSource {
  label: string;
  url: string;
}

export interface ChartCrimeImage {
  src: string;
  alt: string;
  label: string;
  credit: string;
  sourceUrl?: string;
}

export interface ChartCrimeBar {
  label: string;
  value: number;
  displayValue: string;
  drawnValue?: number;
  note?: string;
  highlighted?: boolean;
  leader?: boolean;
  flagged?: boolean;
}

export interface ChartCrimeMethod {
  label: string;
  method: string;
  flagged?: boolean;
}

export interface ChartCrime {
  id: string;
  group: ChartCrimeGroup;
  type: string;
  lab: string;
  date: string;
  title: string;
  finding: string;
  scale?: number;
  bars?: ChartCrimeBar[];
  methods?: ChartCrimeMethod[];
  images?: ChartCrimeImage[];
  sources: ChartCrimeSource[];
}

export const chartCrimes: ChartCrime[] = [
  {
    id: "gpt-5-swe-bench-bars",
    group: "Visual error",
    type: "Geometry",
    lab: "OpenAI",
    date: "2025-08-07",
    title: "Different scores, identical bars",
    finding: "o3 scored 69.1%; GPT-4o scored 30.8%. Their launch-slide bars were the same height.",
    images: [
      {
        src: "/chart-crimes/openai-gpt5-swe-before-after.webp",
        alt: "OpenAI GPT-5 SWE-bench launch chart beside its corrected version",
        label: "Before / after",
        credit: "OpenAI",
        sourceUrl: "https://www.pcgamer.com/software/ai/openais-performance-charts-in-the-gpt-5-launch-video-are-such-a-mess-you-have-to-think-gpt-5-itself-probably-made-them-and-the-companys-attempted-fixes-raise-even-more-questions/",
      },
    ],
    scale: 100,
    bars: [
      { label: "GPT-5", value: 74.9, displayValue: "74.9%" },
      { label: "o3", value: 69.1, displayValue: "69.1%" },
      {
        label: "GPT-4o",
        value: 30.8,
        displayValue: "30.8%",
        drawnValue: 69.1,
        flagged: true,
      },
    ],
    sources: [
      { label: "Launch", url: "https://www.youtube.com/watch?v=0Uu_VJeVVfo" },
      { label: "Corrected", url: "https://openai.com/index/introducing-gpt-5-for-developers/" },
      {
        label: "Report",
        url: "https://www.pcgamer.com/software/ai/openais-performance-charts-in-the-gpt-5-launch-video-are-such-a-mess-you-have-to-think-gpt-5-itself-probably-made-them-and-the-companys-attempted-fixes-raise-even-more-questions/",
      },
    ],
  },
  {
    id: "gpt-5-deception-label",
    group: "Visual error",
    type: "Label",
    lab: "OpenAI",
    date: "2025-08-07",
    title: "Label and bar used different values",
    finding: "GPT-5 was labeled 50%; its bar matched the corrected 16.5% value.",
    images: [
      {
        src: "/chart-crimes/openai-gpt5-deception.png",
        alt: "OpenAI GPT-5 launch slide showing the coding deception bar labeled 50 percent",
        label: "Launch slide",
        credit: "OpenAI",
        sourceUrl: "https://www.youtube.com/watch?v=0Uu_VJeVVfo",
      },
    ],
    scale: 60,
    bars: [
      {
        label: "GPT-5",
        value: 50,
        displayValue: "50% label",
        drawnValue: 16.5,
        note: "16.5% corrected",
        flagged: true,
      },
      { label: "o3", value: 47.4, displayValue: "47.4%" },
    ],
    sources: [
      { label: "Launch", url: "https://www.youtube.com/watch?v=0Uu_VJeVVfo" },
      { label: "Corrected", url: "https://openai.com/index/introducing-gpt-5/" },
      {
        label: "Report",
        url: "https://www.pcgamer.com/software/ai/openais-performance-charts-in-the-gpt-5-launch-video-are-such-a-mess-you-have-to-think-gpt-5-itself-probably-made-them-and-the-companys-attempted-fixes-raise-even-more-questions/",
      },
    ],
  },
  {
    id: "muse-spark-highlight",
    group: "Visual error",
    type: "Emphasis",
    lab: "Meta",
    date: "2026-04-08",
    title: "House model got the winner color",
    finding: "Meta highlighted Muse Spark across the comparison, including rows where another model led.",
    images: [
      {
        src: "/chart-crimes/meta-muse-spark-benchmark-table.png",
        alt: "Meta Muse Spark official benchmark table with Muse Spark values highlighted in blue",
        label: "Official",
        credit: "Meta",
        sourceUrl: "https://ai.meta.com/blog/introducing-muse-spark-msl/",
      },
    ],
    scale: 55,
    bars: [
      { label: "Gemini 3.1 Pro", value: 48.4, displayValue: "48.4", leader: true },
      { label: "GPT-5.4", value: 43.9, displayValue: "43.9" },
      {
        label: "Muse Spark",
        value: 36.5,
        displayValue: "36.5",
        highlighted: true,
        flagged: true,
      },
    ],
    sources: [
      { label: "Official", url: "https://ai.meta.com/blog/introducing-muse-spark-msl/" },
      { label: "X correction", url: "https://x.com/NateSpencerWx/status/2041916343457403371" },
      { label: "Audit", url: "https://museum-chart-crimes.vercel.app/posts/meta-muse-spark-release-graph" },
    ],
  },
  {
    id: "grok-3-consensus",
    group: "Comparison flag",
    type: "Protocol",
    lab: "xAI",
    date: "2025-02-19",
    title: "64 attempts beside one attempt",
    finding: "The AIME chart placed Grok cons@64 beside o3-mini-high @1.",
    images: [
      {
        src: "/chart-crimes/xai-grok3-thread-3.webp",
        alt: "Annotated screenshot of the official xAI AIME 2024 comparison",
        label: "xAI chart",
        credit: "xAI / X critique",
        sourceUrl: "https://x.com/BorisMPower/status/1892407015038996740",
      },
      {
        src: "/chart-crimes/xai-grok3-thread-2.webp",
        alt: "Annotated screenshot of OpenAI's o3-mini AIME methodology note",
        label: "Protocol note",
        credit: "OpenAI / X critique",
        sourceUrl: "https://x.com/BorisMPower/status/1892407015038996740",
      },
    ],
    scale: 100,
    bars: [
      {
        label: "Grok 3 Think",
        value: 93.3,
        displayValue: "93.3%",
        note: "cons@64",
        flagged: true,
      },
      {
        label: "o3-mini-high",
        value: 86.7,
        displayValue: "86.7%",
        note: "@1",
        flagged: true,
      },
    ],
    sources: [
      { label: "Official", url: "https://x.ai/news/grok-3" },
      { label: "X critique", url: "https://x.com/BorisMPower/status/1892407015038996740" },
      { label: "xAI reply", url: "https://x.com/ibab/status/1892418351084732654" },
      { label: "Report", url: "https://techcrunch.com/2025/02/22/did-xai-lie-about-grok-3s-benchmarks/" },
    ],
  },
  {
    id: "kimi-k3-harnesses",
    group: "Comparison flag",
    type: "Harness",
    lab: "Kimi",
    date: "2026-07-16",
    title: "One chart, several agent harnesses",
    finding: "KimiCode, Claude Code, Codex, leaderboards, and cited runs share the comparison.",
    images: [
      {
        src: "/chart-crimes/kimi-k3-coding-benchmarks.webp",
        alt: "Official Kimi K3 coding benchmark comparison chart",
        label: "Official",
        credit: "Kimi",
        sourceUrl: "https://www.kimi.com/blog/kimi-k3",
      },
    ],
    methods: [
      { label: "Kimi K3", method: "KimiCode", flagged: true },
      { label: "Claude", method: "Claude Code", flagged: true },
      { label: "GPT", method: "Codex", flagged: true },
      { label: "Other rows", method: "Leaderboard / cited", flagged: true },
    ],
    sources: [
      { label: "Official", url: "https://www.kimi.com/blog/kimi-k3" },
    ],
  },
  {
    id: "glm-52-hle-scope",
    group: "Comparison flag",
    type: "Scope",
    lab: "Z.ai",
    date: "2026-06-16",
    title: "Text-only and full-set HLE",
    finding: "Unstarred scores use HLE text-only; starred competitor scores use the full set.",
    images: [
      {
        src: "/chart-crimes/glm-52-llm-performance.webp",
        alt: "Official GLM-5.2 multi-panel model performance chart including Humanity's Last Exam",
        label: "Official",
        credit: "Z.ai",
        sourceUrl: "https://z.ai/blog/glm-5.2",
      },
    ],
    scale: 60,
    bars: [
      { label: "Claude Opus 4.8", value: 49.8, displayValue: "49.8*", note: "full set", flagged: true },
      { label: "Gemini 3.1 Pro", value: 45, displayValue: "45.0", note: "text-only", flagged: true },
      { label: "GPT-5.5", value: 41.4, displayValue: "41.4*", note: "full set", flagged: true },
      { label: "GLM-5.2", value: 40.5, displayValue: "40.5", note: "text-only", flagged: true },
    ],
    sources: [
      { label: "Official", url: "https://z.ai/blog/glm-5.2" },
    ],
  },
  {
    id: "sonnet-5-browsecomp-method",
    group: "Comparison flag",
    type: "Methodology",
    lab: "Anthropic",
    date: "2026-06-30",
    title: "Launch chart replaced after method mismatch",
    finding: "Anthropic says the first BrowseComp chart used a simpler method. It replaced it with the system-card setup.",
    images: [
      {
        src: "/chart-crimes/anthropic-sonnet5-browsecomp-before-after.jpg",
        alt: "Original and updated Claude Sonnet 5 BrowseComp cost-performance charts",
        label: "Before / after",
        credit: "Archived comparison",
        sourceUrl: "https://www.reddit.com/r/ClaudeAI/comments/1ukgqwr/looks_like_anthropic_quietly_updated_the_sonnet_5/",
      },
      {
        src: "/chart-crimes/anthropic-sonnet5-browsecomp-corrected.png",
        alt: "Current official Anthropic Claude Sonnet 5 BrowseComp cost-performance chart",
        label: "Current",
        credit: "Anthropic",
        sourceUrl: "https://www.anthropic.com/news/claude-sonnet-5",
      },
    ],
    sources: [
      { label: "Official + changelog", url: "https://www.anthropic.com/news/claude-sonnet-5" },
      {
        label: "Archived comparison",
        url: "https://www.reddit.com/r/ClaudeAI/comments/1ukgqwr/looks_like_anthropic_quietly_updated_the_sonnet_5/",
      },
    ],
  },
  {
    id: "grok-45-provenance",
    group: "Comparison flag",
    type: "Provenance",
    lab: "xAI",
    date: "2026-07-16",
    title: "Own runs beside best-published rivals",
    finding: "xAI combines release runs with competitor values from system cards and leaderboards.",
    methods: [
      { label: "Grok 4.5", method: "xAI run", flagged: true },
      { label: "Competitors", method: "System cards", flagged: true },
      { label: "Competitors", method: "Leaderboards", flagged: true },
    ],
    sources: [
      { label: "Official", url: "https://x.ai/news/grok-4-5" },
    ],
  },
];
